import { ApolloError } from '@apollo/client';
import styled from '@emotion/styled';
import { handleErrorMessage, i18n, NavigationService, showMessage, useSendGeneralTransferDetailsToEmail } from 'src/common';
import { AppRoutes } from 'src/pages/app/_router/app.routes';
import { colors } from 'src/theme';
import { Button, Card, Divider, Flex, Text } from 'src/components';

const FlexStyle = styled(Flex)({
  padding: '0 12px',
});

export type GeneralTransferPaymentKind = 'oneTime' | 'installments';

export interface GeneralTransferPaymentInfoCardProps {
  offerId: string;
  transferType?: GeneralTransferPaymentKind;
}

export const GeneralTransferPaymentInfoCard = ({
  transferType = 'oneTime',
  offerId,
}: GeneralTransferPaymentInfoCardProps) => {
  const [sendGeneralTransferDetailsToEmail] = useSendGeneralTransferDetailsToEmail();

  const handlePay = async () => {
    try {
      await sendGeneralTransferDetailsToEmail({
        offerId,
        installments: transferType === 'installments' ? 12 : 1,
      });
    } catch (error) {
      // Rate-limit denials are toasted by rateLimitErrorLink; everything else here.
      const code = (error as ApolloError)?.graphQLErrors?.[0]?.extensions?.code;
      if (code !== 'RATE_LIMITED' && code !== 'IP_BLOCKED') {
        showMessage({ type: 'error', message: handleErrorMessage(error), duration: 5000, fixed: true });
      }
      return;
    }
    NavigationService.navigate(AppRoutes.CHECKOUT.GENERAL_TRANSFER_PAYMENT_SENT
      .replace(':offerId', offerId)
    );
  };

  return (
    <Flex vertical gap={16}>
      <Card background={colors.success2} radius={8} padding={'4px 8px'}>
        <Flex gap={8} align="flex-start" vertical>
          <Text type="base" bold color={colors.text1}>
            {i18n.t('components.cards.generalTransferPaymentInfoCard.important')}
          </Text>
          <Text type="small" color={colors.text1}>
            {i18n.t('components.cards.generalTransferPaymentInfoCard.text')}{' '}
            <b>{i18n.t('components.cards.generalTransferPaymentInfoCard.text2')}</b>{' '}
            {i18n.t('components.cards.generalTransferPaymentInfoCard.text3')}
          </Text>
        </Flex>
      </Card>
      <Divider dashed color={colors.border3} />
      <FlexStyle gap={12} vertical>
        <Text type="base" bold color={colors.text1}>
          {i18n.t('components.cards.generalTransferPaymentInfoCard.steps')}
        </Text>
        <Flex gap={8} align="flex-start" vertical>
          {[
            i18n.t('components.cards.generalTransferPaymentInfoCard.step1'),
            i18n.t('components.cards.generalTransferPaymentInfoCard.step2'),
            i18n.t('components.cards.generalTransferPaymentInfoCard.step3'),
            i18n.t('components.cards.generalTransferPaymentInfoCard.step4'),
          ].map((step, index) => (
            <Flex key={index} gap={8} align="flex-start">
              <Text type="small" color={colors.text1} style={{ minWidth: '20px' }}>
                {index + 1}.
              </Text>
              <Text type="small" color={colors.text1}>
                {step}
              </Text>
            </Flex>
          ))}
        </Flex>
        {transferType === 'installments' && (
          <Text type="small" color={colors.text1}>
            {i18n.t('components.cards.generalTransferPaymentInfoCard.text4')}
          </Text>
        )}
      </FlexStyle>
      <Button
        btnType="primary"
        text={i18n.t('components.cards.generalTransferPaymentInfoCard.button')}
        onClick={handlePay}
      />
    </Flex>
  );
};